import { inject, Injectable } from '@angular/core';
import { FilterState } from '@ui/shared/domain-filter';
import { debounceTime, delay, map, type Observable, of, shareReplay, Subject, switchMap, take, tap } from 'rxjs';
import { pipeDataResult, type Result } from '@team23/ts-result';
import { AbstractMapIconsService } from '@ui/shared/domain-api';
import { MapDataState, SelectedItemState } from '@ui/shared/domain-map';
import type { Identificable } from '../types/identificable';
import type { Data } from '../types/data';
import { FilterModel } from '@ui/shared/util-filter';
import { ApiDataStatus } from '@ui/shared/util-api';

const initialDelayForReactNativeWebView = 100;
const filterUpdateGracePeriod = 100;

@Injectable()
export class MapItemService<Item extends Data & Identificable = Data & Identificable> {

    protected readonly _newMapItems$ = new Subject<Array<Item>>();

    protected readonly _mapDataState = inject(MapDataState<Item>);
    protected readonly _filterState = inject(FilterState);
    protected readonly _selectedItemState = inject(SelectedItemState<Item>);

    protected readonly _mapIconsService = inject(AbstractMapIconsService);

    public mapItems$ = of(undefined)
        .pipe(
            delay(initialDelayForReactNativeWebView),
            switchMap(() => this._filterState.activeFilters$),
            debounceTime(filterUpdateGracePeriod),
            switchMap<Map<string, FilterModel>, Observable<Result<Array<Item>, ApiDataStatus>>>(
                activeFilters => this._mapDataState.items$({ filters: activeFilters }),
            ),
            pipeDataResult(
                tap(mapItems => {
                    const iconPaths = mapItems.data
                        .map(mapItem => mapItem.iconPath)
                        .filter((value): value is string => !!value);

                    this._mapIconsService.addIconsFromPaths(iconPaths);
                }),
                tap(mapItems => {
                    // Close the currently selected map item if it no longer exists.
                    const currentMapItem = this._selectedItemState.getMapItem();

                    if (
                        currentMapItem
                        && !mapItems.data.some(mapItem => mapItem.uniqueId === currentMapItem.uniqueId)
                    ) {
                        this._selectedItemState.setMapItem(null);
                    }
                }),
                tap(mapItems => {
                    this._newMapItems$.next(mapItems.data);
                }),
            ),
            shareReplay(1),
        );

    public reloadMapData(): void {
        this._mapDataState.reloadMapItems();
    }

    public openMapItemById(mapItem: Identificable): void {
        this.mapItems$
            .pipe(
                take(1),
                map(mapItems => this._getMapItemById(mapItems.data, mapItem)),
            )
            .subscribe(selectedItem => {
                if (selectedItem) {
                    this._selectedItemState.setShowDetailView(false);
                    this._selectedItemState.setMapItem(selectedItem);
                } else {
                    // If the map item can't be found, reset the filters and try once more.
                    this._resetFiltersAndOpenMapItemById(mapItem);
                }
            });
    }

    /**
     * Resets the filters if any filters are set.
     * Also closes the current map item, as it won't exist while the map items update.
     * Then waits for the map items to update, and when they update, find the requested map item in the new items.
     *
     * If it still can't be found, nothing is done.
     * If no filters are set in the first place, nothing is done.
     */
    protected _resetFiltersAndOpenMapItemById(mapItemIdentifier: Identificable): void {
        this._filterState.activeFilters$
            .pipe(
                take(1),
                switchMap(filters => {
                    if (filters.size) {
                        this._selectedItemState.setMapItem(null);

                        /*
                         * Wait just a tick before resetting the filter.
                         * In case the update is very fast,
                         * this prevents a timing issue where the Subject emits before it is observed.
                         */
                        setTimeout(() => {
                            this._filterState.resetActiveFilters();
                        });

                        return this._newMapItems$
                            .pipe(
                                take(1),
                                map(mapItems => this._getMapItemById(mapItems, mapItemIdentifier)),
                            );
                    } else {
                        return of(undefined);
                    }
                }),
            )
            .subscribe(mapItem => {
                if (mapItem) {
                    // Open the found map item after the next redraw, i.e. after the map refreshes.
                    setTimeout(() => {
                        this._selectedItemState.setMapItem(mapItem);
                    });
                }
            });
    }

    protected _getMapItemById(
        mapItems: Array<Item> | undefined,
        mapItemIdentifier: Identificable,
    ): Item | undefined {
        return mapItems?.find(mapItem => mapItem.uniqueId === mapItemIdentifier.uniqueId);
    }

}
